import { supabase } from "@/integrations/supabase/client";

/**
 * Rate Limiting Utilities
 * 
 * Protects against abuse by limiting the number of actions a user can perform
 * within a given time window. Uses Supabase's check_rate_limit function.
 */

export type RateLimitAction = 
  | "auth_login" 
  | "auth_signup" 
  | "auth_password_reset"
  | "community_post"
  | "community_comment"
  | "content_report";

interface RateLimitConfig {
  maxActions: number;
  windowMinutes: number;
  errorMessage: string;
}

const RATE_LIMIT_CONFIGS: Record<RateLimitAction, RateLimitConfig> = {
  auth_login: {
    maxActions: 5,
    windowMinutes: 15,
    errorMessage: "Too many login attempts. Please try again in 15 minutes.",
  },
  auth_signup: {
    maxActions: 3,
    windowMinutes: 60,
    errorMessage: "Too many signup attempts. Please try again in 1 hour.",
  },
  auth_password_reset: {
    maxActions: 3,
    windowMinutes: 60,
    errorMessage: "Too many password reset requests. Please try again in 1 hour.",
  },
  community_post: {
    maxActions: 5,
    windowMinutes: 60,
    errorMessage: "You're posting too quickly. Please wait before creating another post.",
  },
  community_comment: {
    maxActions: 20,
    windowMinutes: 60,
    errorMessage: "You're commenting too quickly. Please wait before commenting again.",
  },
  content_report: {
    maxActions: 10,
    windowMinutes: 60,
    errorMessage: "Too many reports submitted. Please try again later.",
  },
};

/**
 * Check if user has exceeded rate limit for an action
 * @param userId - User ID or fingerprint for anonymous users
 * @param action - Type of action being rate limited
 * @returns Object with allowed status and error message if limit exceeded
 */
export async function checkRateLimit(
  userId: string,
  action: RateLimitAction
): Promise<{ allowed: boolean; error?: string }> {
  const config = RATE_LIMIT_CONFIGS[action];

  try {
    const { data, error } = await supabase.rpc("check_rate_limit", {
      p_user_id: userId,
      p_action_type: action,
      p_max_actions: config.maxActions,
      p_window_minutes: config.windowMinutes,
    });

    if (error) {
      console.error("Rate limit check error:", error);
      // Fail open - allow action if rate limit check fails
      return { allowed: true };
    }

    if (!data) {
      return {
        allowed: false,
        error: config.errorMessage,
      };
    }

    return { allowed: true };
  } catch (error) {
    console.error("Rate limit check exception:", error);
    // Fail open - allow action if exception occurs
    return { allowed: true };
  }
}

/**
 * Get user fingerprint for rate limiting (for non-authenticated users)
 * Uses a combination of browser properties to create a unique identifier
 */
export function getUserFingerprint(): string {
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset(),
    screen.colorDepth,
    screen.width + "x" + screen.height,
  ].join("|");

  // Create a simple hash
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return `fp_${Math.abs(hash).toString(36)}`;
}
